import React, { useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'

/**
 * The preview dialog's copy, in the viewer's language. The dialog is the
 * viewer's interface, not slide content, and LightboxImage sits deep inside
 * plots, so the host app supplies it once through `LightboxLabelsProvider`.
 */
export interface LightboxLabels {
  /** The dialog's name when the image has no alt text. */
  imagePreview: string
  /** The close button's name, for a picture or a text preview. */
  closePreview: string
}

const LightboxLabelsContext = React.createContext<LightboxLabels | null>(null)

export function LightboxLabelsProvider(props: { labels: LightboxLabels; children: React.ReactNode }) {
  return <LightboxLabelsContext.Provider value={props.labels}>{props.children}</LightboxLabelsContext.Provider>
}

function useLightboxLabels(): LightboxLabels {
  const labels = React.useContext(LightboxLabelsContext)
  if (!labels) {
    throw new Error('LightboxImage opened outside a LightboxLabelsProvider: the host app supplies its copy.')
  }
  return labels
}

interface LightboxImageProps {
  src: string
  alt?: string
  /** Styles applied to the trigger <img> element */
  style?: React.CSSProperties
  className?: string
  'data-testid'?: string
}

export function LightboxImage({ src, alt = '', style, className, 'data-testid': testId }: LightboxImageProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      <img
        src={src}
        alt={alt}
        data-testid={testId}
        className={className}
        style={{ ...style, cursor: 'zoom-in' }}
        onClick={() => setOpen(true)}
      />
      {open ? (
        <LightboxDialog label={alt} onClose={() => setOpen(false)}>
          <img
            src={src}
            alt={alt}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
              cursor: 'default',
            }}
          />
        </LightboxDialog>
      ) : null}
    </>
  )
}

/**
 * The open preview: a dimmed page, a close button, and the content (a picture,
 * or a podium tile's whole copy), named by `label` (else the image-preview name). Escape, the button or a click outside the
 * content closes it. Only it carries chrome copy, so only it reads the host's labels.
 */
export function LightboxDialog({ label, onClose, children }: { label?: string; onClose: () => void; children: React.ReactNode }) {
  const labels = useLightboxLabels()
  const close = useCallback(() => onClose(), [onClose])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [close])

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label || labels.imagePreview}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.75)',
        cursor: 'zoom-out',
      }}
    >
      <button
        aria-label={labels.closePreview}
        onClick={close}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: 'rgba(255,255,255,0.15)',
          border: 'none',
          borderRadius: '50%',
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#fff',
          fontSize: 20,
          lineHeight: 1,
        }}
      >
        ✕
      </button>
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'contents' }}>
        {children}
      </div>
    </div>,
    document.body,
  )
}
